import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useCatalogProducts, useCatalogRoles } from '@/features/users'
import {
  assignServiceAccountRole,
  getServiceAccountAccessPreview,
  getServiceAccountAllowedScopes,
  listServiceAccountRoleAssignments,
  putServiceAccountAllowedScopes,
  revokeServiceAccountRole,
} from './api/accessApi'
import {
  deleteServiceAccount,
  listServiceAccounts,
  registerServiceAccount,
  updateServiceAccount,
} from './api/api'
import {
  serviceAccountAccessPreviewKey,
  serviceAccountAllowedScopesKey,
  serviceAccountRoleAssignmentsKey,
  serviceAccountsKeys,
  serviceAccountsListKey,
} from './keyFactory'
import type { ServiceAccountInput } from './types'

// Re-exported so callers only ever import product/catalog-role reads from
// this feature's own hooks module — sharing the users feature's cache is an
// implementation detail (see roles/hooks.ts for the same pattern).
export { useCatalogProducts, useCatalogRoles }

export function useServiceAccounts() {
  return useQuery({ queryKey: serviceAccountsListKey(), queryFn: listServiceAccounts })
}

export function useRegisterServiceAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ServiceAccountInput) => registerServiceAccount(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: serviceAccountsKeys.all() }),
  })
}

export function useUpdateServiceAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ServiceAccountInput }) => updateServiceAccount(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: serviceAccountsKeys.all() }),
  })
}

export function useDeleteServiceAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteServiceAccount(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: serviceAccountsKeys.all() }),
  })
}

// Access tab hooks — the subject-keyed sibling of the users feature's role
// assignment/preview hooks, pointed at the service account's own endpoints.

export function useServiceAccountRoleAssignments(id: string) {
  return useQuery({
    queryKey: serviceAccountRoleAssignmentsKey(id),
    queryFn: () => listServiceAccountRoleAssignments(id),
    enabled: !!id,
  })
}

export function useServiceAccountAccessPreview(id: string, product: string) {
  return useQuery({
    queryKey: serviceAccountAccessPreviewKey(id, product),
    queryFn: () => getServiceAccountAccessPreview(id, product),
    enabled: !!id && !!product,
  })
}

// Assign/revoke both invalidate the assignments list AND the effective-access
// preview for the affected product, same as the users feature's equivalents.
export function useAssignServiceAccountRole(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ product, roleId }: { product: string; roleId: string }) => assignServiceAccountRole(id, product, roleId),
    onSuccess: (_data, { product }) => {
      queryClient.invalidateQueries({ queryKey: serviceAccountRoleAssignmentsKey(id) })
      queryClient.invalidateQueries({ queryKey: serviceAccountAccessPreviewKey(id, product) })
    },
  })
}

export function useRevokeServiceAccountRole(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ product, roleId }: { product: string; roleId: string }) => revokeServiceAccountRole(id, product, roleId),
    onSuccess: (_data, { product }) => {
      queryClient.invalidateQueries({ queryKey: serviceAccountRoleAssignmentsKey(id) })
      queryClient.invalidateQueries({ queryKey: serviceAccountAccessPreviewKey(id, product) })
    },
  })
}

export function useServiceAccountAllowedScopes(id: string) {
  return useQuery({
    queryKey: serviceAccountAllowedScopesKey(id),
    queryFn: () => getServiceAccountAllowedScopes(id),
    enabled: !!id,
  })
}

export function usePutServiceAccountAllowedScopes(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (scopeIds: string[]) => putServiceAccountAllowedScopes(id, scopeIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: serviceAccountAllowedScopesKey(id) }),
  })
}
